import { AfterContentInit, Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { filter, finalize } from 'rxjs/operators';

import { untilDestroyed } from '@app/core';
import { AppService, ErrorHandlerService, FolderService, UserService } from '@app/services';
import { ContextEnum, Folder, SlimDocument, User } from '@app/models';
import { LoaderService } from '@app/shared/loader/loader.service';
import { SearchComponent } from '@app/shared/search/search.component';
import { CreateFolderModalService } from '../create-folder-modal/create-folder-modal.service';

export type ModalResult = Folder;
export interface ModalPublicProperties {
  itemsToMove: SlimDocument[] | Folder;
  currentFolderId?: string;
  targetType?: 'document' | 'folder';
  isNewDocument?: boolean;
  context?: ContextEnum;
}

@Component({
  selector: 'app-folders-modal',
  templateUrl: './folders-modal.component.html',
  styleUrls: ['./folders-modal.component.scss'],
  standalone: false
})
export class FoldersModalComponent implements ModalPublicProperties, AfterContentInit, OnDestroy {
  @Input() itemsToMove: SlimDocument[] | Folder;
  @Input() currentFolderId: string;
  @Input() targetType: ModalPublicProperties['targetType'] = 'document';
  @Input() isNewDocument: false;
  @Input() context?: ContextEnum;
  @ViewChild('searchComponent', { static: false }) searchComponent: SearchComponent;
  currentUser: User;
  isLoading = false;
  isLastPage = false;
  pageNumber: number;
  search: string;
  folders: Folder[];
  selectedFolder: Folder;
  parentFolder: Folder;

  constructor(
    public modal: NgbActiveModal,
    public appService: AppService,
    private loaderService: LoaderService,
    private userService: UserService,
    private folderService: FolderService,
    private errorHandlerService: ErrorHandlerService,
    private createFolderModalService: CreateFolderModalService
  ) {}

  ngAfterContentInit() {
    this.userService
      .watchCurrentUser()
      .pipe(
        filter(user => !!user),
        untilDestroyed(this)
      )
      .subscribe({
        next: user => {
          this.currentUser = user;
          this.searchFolders();
        }
      });
  }

  ngOnDestroy() {}

  searchFolders(options: { search?: string; pageNumber?: number; parentId?: string } = {}) {
    if (this.currentUser) {
      this.isLoading = true;
      this.loaderService.disableHttpLoader = true;
      this.pageNumber = options.pageNumber || 1;
      this.folders = this.pageNumber === 1 ? [] : this.folders;
      const parentId = options.hasOwnProperty('parentId') ? options.parentId : this.parentFolder?.id || null;

      const params = { search: options.search || '', page: this.pageNumber, limit: 12, parentId };
      return this.folderService
        .folders(params)
        .pipe(
          finalize(() => {
            this.isLoading = false;
            this.loaderService.disableHttpLoader = false;
          })
        )
        .subscribe({
          next: page => {
            this.folders = (this.pageNumber === 1 ? [] : this.folders).concat(page.data).filter(folder => this.targetType !== 'folder' || (this.itemsToMove as Folder)?.id !== folder.id);
            this.isLastPage = !page || !page.has_more_pages;
          },
          error: error => this.errorHandlerService.handle(error)
        });
    }
  }

  moveToFolder(folder?: Folder) {
    if (this.currentFolderId === folder?.id) {
      this.modal.dismiss();
      return;
    }

    this.isLoading = true;

    let moveToFolder$;
    if (this.targetType === 'folder') {
      const variables = {
        id: (this.itemsToMove as Folder).id,
        ...(folder && { parentFolderId: folder.id })
      };

      moveToFolder$ = this.folderService.moveFolder(variables);
    } else {
      const currentFolderId = this.currentFolderId;
      const documentsIds = (this.itemsToMove as SlimDocument[]).map((item: { id: string }) => item?.id);

      moveToFolder$ = folder
        ? this.folderService.moveDocumentsToFolder({ documentsIds, folderId: folder.id, ...(currentFolderId ? { currentFolderId } : {}), context: this.context })
        : this.folderService.removeDocumentsFromFolder({ documentsIds, currentFolderId });
    }

    moveToFolder$.pipe(finalize(() => (this.isLoading = false))).subscribe({
      next: () => this.modal.close(folder as ModalResult),
      error: error => this.errorHandlerService.handle(error)
    });
  }

  openCreateFolderModal() {
    if (this.isLoading) return;
    this.isLoading = true;

    this.createFolderModalService
      .open({ parentId: this.selectedFolder?.id || null })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: data => {
          if (!this.selectedFolder || (this.selectedFolder && this.parentFolder && this.selectedFolder.id === this.parentFolder.id)) {
            (this.folders || []).unshift(data);
          } else {
            this.selectedFolder.children_counter = (this.selectedFolder.children_counter || 0) + 1;
          }
        }
      });
  }

  selectFolder(folder?: Folder, isParent?: boolean) {
    if (this.isLoading || (this.selectedFolder && folder && this.selectedFolder.id === folder.id)) return;

    this.selectedFolder = folder;
    if (!isParent && this.selectedFolder !== null && (!this.selectedFolder || this.selectedFolder.children_counter > 0)) {
      this.search = this.selectedFolder?.id ? '' : this.search;
      this.folders = [];
      this.parentFolder = undefined;
      this.searchFolders({ pageNumber: 1, parentId: this.selectedFolder?.id || null });
      if (this.selectedFolder) {
        this.folderService.folder({ id: this.selectedFolder.id }).subscribe({
          next: selectedFolder => {
            this.parentFolder = selectedFolder;
            this.selectedFolder = selectedFolder;
          }
        });
      }
    }
  }

  focusSearchInput() {
    if (window.innerWidth > 992) {
      this.searchComponent.focus();
    }
  }

  targetFolder(folder?: Folder) {
    if (this.isLoading && folder) return;
    this.isLoading = true;
    this.modal.close(folder as ModalResult);
  }
}
